// src/services/activeTenant.js
//
// Phase 6 — Framework-agnostic "active tenant / site" store.
//
// This is the single source of truth for which tenant + site the app is
// currently operating on. It lives outside React so that plain service files
// (studyService, coordinatorService, patientAuditService, ...) can resolve
// Firestore paths too — not just components. TenantContext (React) is a thin
// reactive wrapper around this store.

const TENANT_KEY = "activeTenantId";
const SITE_KEY = "activeSiteId";

export const DEFAULT_TENANT_ID =
  process.env.REACT_APP_DEFAULT_TENANT || "lung-innovations";
export const DEFAULT_SITE_ID =
  process.env.REACT_APP_DEFAULT_SITE || "lin-dubois1";

const read = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback; // private mode / non-browser
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* non-fatal */
  }
};

const remove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
};

let _tenantId = read(TENANT_KEY, DEFAULT_TENANT_ID);
let _siteId = read(SITE_KEY, DEFAULT_SITE_ID);

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn({ tenantId: _tenantId, siteId: _siteId }));

export const getActiveTenantId = () => _tenantId;
export const getActiveSiteId = () => _siteId;
export const getActiveTenant = () => ({ tenantId: _tenantId, siteId: _siteId });

export const setActiveTenantId = (id) => {
  if (!id || id === _tenantId) return;
  _tenantId = id;
  write(TENANT_KEY, id);
  notify();
};

export const setActiveSiteId = (id) => {
  if (!id || id === _siteId) return;
  _siteId = id;
  write(SITE_KEY, id);
  notify();
};

export const setActiveTenantSite = (tenantId, siteId) => {
  if (tenantId) setActiveTenantId(tenantId);
  if (siteId) setActiveSiteId(siteId);
};

/**
 * Reset the active tenant/site back to the defaults — call this on LOGOUT.
 *
 * Clearing localStorage alone is NOT enough. `_tenantId` / `_siteId` are read
 * from storage once, when this module is first imported, and the app is a SPA:
 * navigating to /login re-renders, it does not reload the module. So a logout
 * that only wiped storage left the previous user's site live in memory, and on
 * a shared workstation the next person to sign in started on it — reading and
 * writing that site's data until a hard refresh.
 *
 * Resets the in-memory values too, and notifies subscribers so TenantContext
 * follows. Unconditional (unlike the setters, which no-op when unchanged), so
 * the persisted keys are always removed even if the values already match.
 */
export const clearActiveTenant = () => {
  remove(TENANT_KEY);
  remove(SITE_KEY);
  _tenantId = DEFAULT_TENANT_ID;
  _siteId = DEFAULT_SITE_ID;
  notify();
};

/** Subscribe to changes; returns an unsubscribe fn. */
export const subscribeActiveTenant = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// ---------------------------------------------------------------------------
// Legacy Google Sheets backend — Dubois-only.
// The single Google Sheet (getPatientsFromSheet / logToSheet / ...) holds
// patient data for ONE site only (lin-dubois1). It is not multi-tenant. Use
// this guard so the Sheet is only read on that site; every other site is
// Firestore-only and shows no Sheet data.
// ---------------------------------------------------------------------------
export const LEGACY_SHEET_TENANT_ID =
  process.env.REACT_APP_LEGACY_SHEET_TENANT || DEFAULT_TENANT_ID;
export const LEGACY_SHEET_SITE_ID =
  process.env.REACT_APP_LEGACY_SHEET_SITE || DEFAULT_SITE_ID;

export const activeSiteUsesLegacySheet = () =>
  _tenantId === LEGACY_SHEET_TENANT_ID && _siteId === LEGACY_SHEET_SITE_ID;

// ---------------------------------------------------------------------------
// Legacy Research Calendar — Dubois-only, same reasoning as the Sheet above.
//
// REACT_APP_CALENDAR_API is a SINGLE Apps Script web app bound to ONE calendar.
// It has no concept of sites. So when the calendar proxy is off and the browser
// posts to it directly, EVERY site would read and write Dubois's calendar —
// another site's coordinators would see Dubois patients, and their bookings
// would land on Dubois's calendar. Guard the direct path with this the way
// activeSiteUsesLegacySheet() guards the Sheet.
//
// Mirrors isLegacyCalendarSite() in functions/index.js — keep them in step.
// ---------------------------------------------------------------------------
export const LEGACY_CALENDAR_TENANT_ID =
  process.env.REACT_APP_LEGACY_CALENDAR_TENANT || DEFAULT_TENANT_ID;
export const LEGACY_CALENDAR_SITE_ID =
  process.env.REACT_APP_LEGACY_CALENDAR_SITE || DEFAULT_SITE_ID;

export const activeSiteUsesLegacyCalendar = () =>
  _tenantId === LEGACY_CALENDAR_TENANT_ID && _siteId === LEGACY_CALENDAR_SITE_ID;
